import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SimilarReadersFeed } from '@/components/SimilarReadersFeed';
import { DiscoverTour } from '@/components/DiscoverTour';
import { Sparkles, TrendingUp, BookOpen, RefreshCw, History, Award, Zap } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDiscoverTour } from '@/hooks/useDiscoverTour';
import { useState } from 'react';
import { CoverImage } from '@/components/CoverImage';
import { StarRating } from '@/components/StarRating';
import { BookRecommendationCard } from '@/components/BookRecommendationCard';
import { SmartBookChat } from '@/components/SmartBookChat';
import { useCachedRecommendations } from '@/hooks/useCachedRecommendations';
import { useBookRecommendations } from '@/hooks/useBookRecommendations';
import { useTrendingBooks } from '@/hooks/useTrendingBooks';
import { useAwardBookRecommendations } from '@/hooks/useAwardBookRecommendations';
import { Badge } from '@/components/ui/badge';
import { AuthorFollowSuggestions } from '@/components/AuthorFollowSuggestions';
import { BookRecommendationCardSkeleton } from '@/components/BookRecommendationCardSkeleton';
import { cn } from '@/lib/utils';

interface TrendingBook {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  cover_cache_url?: string;
  rating_count: number;
  average_rating: number;
}

export default function Discover() {
  const { user } = useAuth();
  const { shouldShowTour, completeTour } = useDiscoverTour();
  const [refreshKey, setRefreshKey] = useState(0);
  const [useFallback, setUseFallback] = useState(false);
  
  // Try cached recommendations first
  const { 
    data: cachedData, 
    isLoading: loadingCached, 
    refresh: refreshCached,
    isFetching: isFetchingCached 
  } = useCachedRecommendations(6);
  
  // Fallback to direct API call if cache is empty
  const { 
    data: fallbackData, 
    isLoading: loadingFallback, 
    refetch: refetchFallback 
  } = useBookRecommendations(refreshKey);
  
  // Use cached data if available, otherwise use fallback
  const hasCachedRecs = cachedData?.recommendations && cachedData.recommendations.length > 0;
  const recommendations = hasCachedRecs ? cachedData.recommendations : (fallbackData?.recommendations || []);
  const classicRecommendation = hasCachedRecs ? cachedData.classicRecommendation : fallbackData?.classicRecommendation;
  const loadingRecs = loadingCached || (cachedData?.cacheCount === 0 && loadingFallback);
  const fromCache = cachedData?.fromCache && hasCachedRecs;
  const aiErrorMessage = fallbackData?.errorMessage;

  const { data: trendingBooks = [], isLoading: loadingTrending } = useTrendingBooks();
  const { data: awardRecs = [], isLoading: loadingAwards, refetch: refetchAwards } = useAwardBookRecommendations();
  const [dismissedCount, setDismissedCount] = useState(0);

  const handleRefresh = async () => {
    setRefreshKey(prev => prev + 1);
    setDismissedCount(0);
    
    // Trigger cache refresh
    await refreshCached();
    
    // Also refetch fallback in case cache is empty
    refetchFallback();
  };

  const handleDismiss = () => {
    const newCount = dismissedCount + 1;
    setDismissedCount(newCount);
    
    // If we've dismissed many recommendations, refetch
    const totalRecs = recommendations.length;
    if (totalRecs - newCount < 4) {
      console.log('📊 Refetching: too many dismissals');
      handleRefresh();
    }
  };


  return (
    <div className="container max-w-7xl mx-auto py-8 px-4 space-y-8 pb-20 md:pb-8">
      {shouldShowTour && <DiscoverTour onComplete={completeTour} />}
      
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold tracking-tight">Discover</h1>
        <p className="text-muted-foreground text-lg">
          AI-powered recommendations based on your reading history
        </p>
      </div>

      {/* AI Recommendations - Top Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <CardTitle>Recommended For You</CardTitle>
              {fromCache && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Zap className="w-3 h-3" />
                  Instant
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh}
                disabled={loadingRecs}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingRecs ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to="/recommendations/history">
                  <History className="w-4 h-4 mr-2" />
                  History
                </Link>
              </Button>
            </div>
          </div>
          <CardDescription>
            Based on your reviews and ratings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cachedData?.needsMoreReviews ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-primary/60 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">
              Please rate more books to unlock this feature
            </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                We need to learn your taste first! Rate a few books so our AI can suggest personalized picks just for you.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="flex gap-1">
                  {Array.from({ length: cachedData.requiredCount || 3 }).map((_, i) => (
                    <div 
                      key={i} 
                      className={cn(
                        "w-3 h-3 rounded-full",
                        i < (cachedData.reviewCount || 0) ? "bg-primary" : "bg-muted"
                      )}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  {cachedData.reviewCount || 0}/{cachedData.requiredCount || 3} books rated
                </span>
              </div>
              <Button asChild>
                <Link to="/search">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Find Books to Rate
                </Link>
              </Button>
            </div>
          ) : loadingRecs ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <BookRecommendationCardSkeleton key={i} />
              ))}
            </div>
          ) : aiErrorMessage ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-destructive mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">Recommendations temporarily unavailable</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                {aiErrorMessage}
              </p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          ) : recommendations && Array.isArray(recommendations) && recommendations.length > 0 ? (
            <div className={cn(
              "grid gap-4 auto-rows-fr",
              recommendations.length === 1 && "grid-cols-1 max-w-[200px] mx-auto",
              recommendations.length === 2 && "grid-cols-2 max-w-[420px] mx-auto",
              recommendations.length === 3 && "grid-cols-3 max-w-[640px] mx-auto",
              recommendations.length === 4 && "grid-cols-2 sm:grid-cols-4 max-w-[860px] mx-auto",
              recommendations.length >= 5 && "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
            )}>
              {recommendations.slice(0, 5).map((rec, idx) => (
                <div key={idx} className="h-full">
                  <BookRecommendationCard 
                    {...rec}
                    onDismiss={handleDismiss}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                {aiErrorMessage ? "Recommendations temporarily unavailable" : "No recommendations available"}
              </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                {aiErrorMessage 
                  ? aiErrorMessage
                  : refreshKey > 0 
                    ? "Try refreshing again to discover new books"
                    : "Rate and review more books to get personalized AI recommendations tailored to your taste"}
              </p>
              <Button onClick={handleRefresh} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                {aiErrorMessage ? "Try Again" : "Refresh Recommendations"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Award Recommendations & Classic Book - Side by Side */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Award Recommendations */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <CardTitle>Award-Winning Picks</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => refetchAwards()}
                  disabled={loadingAwards}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingAwards ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/awards/matches">
                    View All
                  </Link>
                </Button>
              </div>
            </div>
            <CardDescription>
              Award winners and nominees matched to your reading preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAwards ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <BookRecommendationCardSkeleton key={i} />
                ))}
              </div>
            ) : awardRecs.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-fr">
                {awardRecs.slice(0, 4).map((rec, index) => (
                  <div key={index} className="h-full">
                    <BookRecommendationCard
                      title={rec.title}
                      author={rec.author}
                      reason={rec.reason}
                      bookId={rec.bookId}
                      coverUrl={rec.coverUrl}
                      confidenceScore={rec.match_score}
                      matchDetails={rec.awardName ? `${rec.awardName}${rec.awardYear ? ` ${rec.awardYear}` : ''}` : undefined}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No award recommendations available yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classic Book Recommendation */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <CardTitle>A Classic For You</CardTitle>
              <Badge variant="secondary" className="ml-auto">Timeless</Badge>
            </div>
            <CardDescription>
              A literary classic matched to your reading taste
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingRecs ? (
              <BookRecommendationCardSkeleton />
            ) : classicRecommendation ? (
              <BookRecommendationCard 
                {...classicRecommendation}
                onDismiss={handleDismiss}
              />
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No classic recommendation yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Smart Book Chat */}
      <SmartBookChat />

      {/* Author Follow Suggestions */}
      <AuthorFollowSuggestions />

      {/* Main Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Similar Readers */}
        <SimilarReadersFeed />

        {/* Trending Books */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              <CardTitle>Trending Books</CardTitle>
            </div>
            <CardDescription>
              Most popular books on Dino right now
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTrending ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-12 h-16 bg-muted rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : trendingBooks.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No trending books yet. Start rating books to see what's popular!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {trendingBooks.map((book) => (
                  <Link
                    key={book.id}
                    to={`/book/${book.id}`}
                    className="flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="shrink-0">
                      <CoverImage
                        bookId={book.id}
                        title={book.title}
                        author={book.authors?.[0]}
                        coverUrl={book.cover_url}
                        coverCacheUrl={book.cover_cache_url}
                        size="xs"
                      />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <h4 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-2">
                        {book.title}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {book.authors?.[0] || 'Unknown Author'}
                      </p>
                      <div className="flex items-center gap-2">
                        <StarRating value={book.average_rating} size="sm" readonly showLabel={false} />
                        <span className="text-xs text-muted-foreground">
                          ({book.rating_count} {book.rating_count === 1 ? 'rating' : 'ratings'})
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
